import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { fetchDebug, clearDebug, type DebugEntry } from '../api';
import { Button } from './Button';

interface Props {
  open: boolean;
  onClose: () => void;
}

function levelColor(level: DebugEntry['level']): string {
  if (level === 'error') return 'text-rose-300';
  if (level === 'warn') return 'text-amber-300';
  return 'text-slate-300';
}

export function DebugPanel({ open, onClose }: Props) {
  const [entries, setEntries] = useState<DebugEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchDebug();
      setEntries(res.entries ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load debug log');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  if (!open) return null;

  const errors = entries.filter((e) => e.level === 'error').length;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-black/70 p-0 sm:items-center sm:p-4">
      <div className="flex h-[80vh] w-full max-w-3xl flex-col rounded-2xl border border-zinc-700 bg-zinc-900 shadow-2xl">
        <div className="flex items-center justify-between border-b border-zinc-800 px-4 py-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-100">🐞 Debug statements</h3>
            <p className="text-[10px] text-zinc-500">
              {entries.length} entries{errors > 0 ? ` · ${errors} errors` : ''} — server-side logs only
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => void load()}>Refresh</Button>
            <Button variant="ghost" size="sm" onClick={() => void clearDebug().then(load)}>Clear</Button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg px-2 py-1 text-xs font-semibold text-slate-500 hover:text-slate-300"
            >
              Close
            </button>
          </div>
        </div>

        {error && (
          <p className="m-3 rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-300">
            {error}
          </p>
        )}

        <div className="flex-1 space-y-1.5 overflow-y-auto px-3 py-3 font-mono text-[11px] leading-relaxed">
          {loading ? (
            <p className="text-zinc-500">Loading…</p>
          ) : entries.length === 0 ? (
            <p className="text-zinc-600">No debug statements captured yet. Trigger an upload or AI generation, then refresh.</p>
          ) : (
            [...entries].reverse().map((e, i) => (
              <div key={i} className="rounded-md border border-zinc-800/60 bg-zinc-950/50 px-2.5 py-1.5">
                <span className="text-zinc-600">
                  {new Date(e.ts).toLocaleTimeString()} ·{' '}
                </span>
                <span className="font-semibold text-cyan-400">{e.source}</span>
                <span className={`ml-1 ${levelColor(e.level)}`}>
                  {e.message}
                </span>
                {e.data !== undefined && (
                  <pre className="mt-1 overflow-x-auto whitespace-pre-wrap text-zinc-500">
                    {typeof e.data === 'string' ? e.data : JSON.stringify(e.data, null, 2)}
                  </pre>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
